import type { ThresholdColors, TracePalette } from '@ui/Types/Gnss';

export function colorForValue(v: number, offsetDb: number): string {
    const clamped = Math.max(-120, Math.min(0, v + offsetDb));
    const t = (clamped + 120) / 120; // 0..1
    const y = Math.floor(255 * t);
    const r = Math.floor(y);
    const g = Math.floor(y);
    const b = Math.floor(40 + 80 * (1 - t));
    return 'rgb(' + r.toString() + ',' + g.toString() + ',' + b.toString() + ')';
}

export function openSnapshotDb(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
        const req = window.indexedDB.open('sweepSnapshots', 3);
        req.onupgradeneeded = () => {
            const db = req.result;
            const stores = ['tiles_rx1', 'tiles_rx2', 'meta_rx1', 'meta_rx2'];
            for (const name of stores) {
                if (!db.objectStoreNames.contains(name)) {
                    db.createObjectStore(name);
                }
            }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error ?? new Error('IndexedDB open failed'));
    });
}

export function idbPutBlob(db: IDBDatabase, store: string, key: number, blob: Blob): Promise<void> {
    return new Promise((resolve, reject) => {
        try {
            const tx = db.transaction(store, 'readwrite');
            const st = tx.objectStore(store);
            const req = st.put(blob, key);
            req.onsuccess = () => resolve();
            req.onerror = () => reject(req.error ?? new Error('IndexedDB put error'));
        } catch (error) {
            reject(error instanceof Error ? error : new Error('idbPutBlob failed'));
        }
    });
}

export function idbDelete(db: IDBDatabase, store: string, key: number): Promise<void> {
    return new Promise((resolve, reject) => {
        try {
            const tx = db.transaction(store, 'readwrite');
            const st = tx.objectStore(store);
            const req = st.delete(key);
            req.onsuccess = () => resolve();
            req.onerror = () => reject(req.error ?? new Error('IndexedDB delete error'));
        } catch (error) {
            reject(error instanceof Error ? error : new Error('idbDelete failed'));
        }
    });
}

export function idbGetCount(db: IDBDatabase, store: string): Promise<number> {
    return new Promise((resolve, reject) => {
        try {
            const tx = db.transaction(store, 'readonly');
            const st = tx.objectStore(store);
            const req = st.count();
            req.onsuccess = () => resolve(req.result);
            req.onerror = () => reject(req.error ?? new Error('IndexedDB count error'));
        } catch (error) {
            reject(error instanceof Error ? error : new Error('idbGetCount failed'));
        }
    });
}

export function canvasToWebPBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
    return new Promise((resolve, reject) => {
        canvas.toBlob(
            (blob) => {
                if (blob) {
                    resolve(blob);
                    return;
                }
                try {
                    const dataUrl = canvas.toDataURL('image/png');
                    const base64 = dataUrl.split(',')[1] ?? '';
                    const bytes = atob(base64);
                    const ia = new Uint8Array(bytes.length);
                    for (let i = 0; i < bytes.length; i++) {
                        ia[i] = bytes.charCodeAt(i);
                    }
                    resolve(new Blob([ia], { type: 'image/png' }));
                } catch (error) {
                    reject(error instanceof Error ? error : new Error('Encoding fallback failed'));
                }
            },
            'image/webp',
            quality,
        );
    });
}

// Distinct latest colors per RX, shared colors for average and peak to aid recognition
export const RX1_PALETTE: TracePalette = {
    average: '#4caf50', // green for averages
    latest: '#ffcc00', // yellow for RX1 latest
    peak: '#f06292', // pink for peaks to avoid clash with red alerts
};

export const RX2_PALETTE: TracePalette = {
    average: '#4caf50', // same green for averages
    latest: '#00bcd4', // cyan for RX2 latest
    peak: '#f06292', // same pink for peaks
};

// Baseline overlay color
export const BASELINE_COLOR = '#90caf9';

// Alert threshold lines on spectrum overlays
// Medium must be orange, High must be red
export const THRESHOLD_COLORS: ThresholdColors = {
    high: '#e53935',
    medium: '#ff9800',
};
